/**
 * LoyaltyProgram — Page de configuration du programme de fidélité (V2 / B1)
 *
 * Compose :
 *   - TypeSelector (Points / Tampons / Cagnotte-prochainement / Missions-prochainement)
 *   - PointsConfig (générosité + récompense principale + paliers additionnels)
 *   - StampsConfig (objectif + récompense finale + récompenses intermédiaires)
 *   - GradesConfig (niveaux de fidélité avec avantages cochables)
 *   - BoostConfig (boost de démarrage 3 modes)
 *
 * Backward-compat : on lit/écrit les anciens champs (spendingRatio, targetCount,
 * welcomeBonus, rewardLabel, missions) en parallèle des nouveaux pour que le
 * runtime de crédit (validateQrSession etc.) continue de fonctionner sans
 * modification — ces nouveaux champs seront consommés en B2.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { SpinnerIcon } from './icons/SpinnerIcon';
import type {
    LoyaltyProgramType,
    LoyaltyConfig,
    LoyaltyGrade,
    LoyaltyBoost,
    RewardTier,
} from '../../shared/types';
import { getBackendService } from '../../shared/services/apiConfig';
import TypeSelector from './loyalty/TypeSelector';
import PointsConfig, { type PointsConfigState } from './loyalty/PointsConfig';
import StampsConfig, { type StampsConfigState } from './loyalty/StampsConfig';
import GradesConfig from './loyalty/GradesConfig';
import BoostConfig from './loyalty/BoostConfig';
import { Button, PageHeader } from '../../shared/design';
import Modal from '../../shared/design/components/Modal';
import ProgramPreview from './loyalty/ProgramPreview';
import { CheckIcon, XIcon, WarningIcon } from './icons/UiIcons';

// ── Default values ───────────────────────────────────────────────────────────

const DEFAULT_GRADES: LoyaltyGrade[] = [
    { id: 'bronze', name: 'Bronze', threshold: 0, color: '#D9A27A', advantages: [] },
    { id: 'silver', name: 'Argent', threshold: 50, color: '#C9CED6', advantages: [] },
    { id: 'gold', name: 'Or', threshold: 150, color: '#E6C35C', advantages: [] },
];

const DEFAULT_GRADES_STAMPS: LoyaltyGrade[] = [
    { id: 'bronze', name: 'Bronze', threshold: 0, color: '#D9A27A', advantages: [] },
    { id: 'silver', name: 'Argent', threshold: 5, color: '#C9CED6', advantages: [] },
    { id: 'gold', name: 'Or', threshold: 15, color: '#E6C35C', advantages: [] },
];

const DEFAULT_BOOST: LoyaltyBoost = { enabled: false };

/** Anciens programmes : niveaux au même identifiant (ajout après suppression) → identifiants uniques. */
const dedupeGradeIds = (grades: LoyaltyGrade[]): LoyaltyGrade[] => {
    const seen = new Set<string>();
    return grades.map((g, i) => {
        let id = g.id || `grade-${i + 1}`;
        while (seen.has(id)) id = `${id}-${i + 1}`;
        seen.add(id);
        return id === g.id ? g : { ...g, id };
    });
};

// ── Migration helper : convert grade thresholds Points ↔ Tampons ─────────────

const migrateGradeThresholds = (
    grades: LoyaltyGrade[],
    fromType: LoyaltyProgramType,
    toType: LoyaltyProgramType,
): LoyaltyGrade[] => {
    if (fromType === toType) return grades;
    const ratio = 10; // 10 pts ≈ 1 visite
    return grades.map(g => {
        if (g.threshold === 0) return g; // Bronze par défaut reste à 0
        let newThreshold = g.threshold;
        if (fromType === 'points' && toType === 'stamps') {
            newThreshold = Math.max(1, Math.round(g.threshold / ratio));
        } else if (fromType === 'stamps' && toType === 'points') {
            newThreshold = g.threshold * ratio;
        }
        return { ...g, threshold: newThreshold };
    });
};

// ── Étape repliable du configurateur ─────────────────────────────────────────
// Défini hors de LoyaltyProgram : un composant créé dans le rendu serait
// recréé à chaque frappe et ferait perdre le focus aux champs.
const Step: React.FC<{
    n: number; title: string; hint: string; summary: string; errors?: string[];
    open: boolean; onToggle: () => void; children: React.ReactNode;
}> = ({ n, title, hint, summary, errors, open, onToggle, children }) => (
    <section className={`rounded-3xl border bg-white shadow-sm transition-colors ${open ? 'border-brand-200' : 'border-black/[0.06]'}`}>
        <button type="button" onClick={onToggle} aria-expanded={open} className="flex w-full items-center gap-4 px-5 py-4 text-left">
            <span className={`inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full font-display text-sm ${open ? 'bg-brand-500 text-cream' : errors?.length ? 'bg-brand-50 text-brand-600' : 'bg-cream-200 text-gray-900'}`}>{n}</span>
            <span className="min-w-0 flex-1">
                <span className="block font-semibold text-gray-900">{title}</span>
                <span className={`block truncate text-sm ${errors?.length && !open ? 'text-brand-600' : 'text-gray-500'}`}>
                    {open ? hint : (errors?.length ? `${errors.length} point(s) à corriger` : summary)}
                </span>
            </span>
            <svg className={`h-5 w-5 flex-shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
        </button>
        {open && <div className="border-t border-black/[0.06] px-5 py-5 animate-fade-in">{children}</div>}
    </section>
);

// ── Component ────────────────────────────────────────────────────────────────

const LoyaltyProgram: React.FC = () => {
    const [config, setConfig] = useState<LoyaltyConfig>({
        type: 'points',
        spendingRatio: 1,
        generosity: 1,
        targetCount: 50,
        welcomeBonus: 0,
        rewardLabel: 'Café offert',
        rewardTiers: [],
        grades: DEFAULT_GRADES,
        boost: DEFAULT_BOOST,
    });
    const [initialType, setInitialType] = useState<LoyaltyProgramType>('points');
    const [isLoading, setIsLoading] = useState(true);
    const [restaurantName, setRestaurantName] = useState('Votre restaurant');
    const [openStep, setOpenStep] = useState<number>(1);
    const [showPreview, setShowPreview] = useState(false);
    const [dirty, setDirty] = useState(false);

    const [isMigrationModalOpen, setIsMigrationModalOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [saveResult, setSaveResult] = useState<string | null>(null);
    const [migrationToast, setMigrationToast] = useState<string | null>(null);
    const [loaded, setLoaded] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [pendingMigrationFrom, setPendingMigrationFrom] = useState<string | null>(null);
    const [retryingMigration, setRetryingMigration] = useState(false);
    const [migrationRetryResult, setMigrationRetryResult] = useState<string | null>(null);
    const [checkingBeforeSave, setCheckingBeforeSave] = useState(false);

    const isLegacyType = config.type === 'spending' || config.type === 'missions';

    // Modifications non publiées (après le chargement initial)
    const firstRender = React.useRef(true);
    useEffect(() => {
        if (isLoading) return;
        if (firstRender.current) { firstRender.current = false; return; }
        setDirty(true);
    }, [config, isLoading]);

    // ── Load existing program ─────────────────────────────────────────────────
    // Échec de lecture : bandeau + « Réessayer », et « Publier » reste bloqué — publier sur
    // un état inconnu écraserait le programme réel (ou déclencherait une conversion à tort).
    const loadProgram = React.useCallback(async () => {
        setIsLoading(true);
        setLoadError(null);
            try {
                const backend = await getBackendService();
                const user = backend.getCurrentUser();
                if (!user?.restaurantId) { setIsLoading(false); return; }
                backend.getRestaurant(user.restaurantId).then((r: any) => { if (r?.name) setRestaurantName(r.name); }).catch(() => {});
                const program = await (backend as any).getLoyaltyProgram(user.restaurantId);
                setPendingMigrationFrom(typeof program?.pendingMigrationFrom === 'string' ? program.pendingMigrationFrom : null);
                if (program) {
                    const type = (program.type || 'points') as LoyaltyProgramType;
                    const stampsTarget = program.targetCount && program.targetCount >= 5 && program.targetCount <= 20
                        ? program.targetCount
                        : 10;
                    const pointsTarget = program.targetCount && program.targetCount >= 10
                        ? program.targetCount
                        : 50;
                    const generosity = program.generosity ?? program.spendingRatio ?? 1;
                    const loaded: LoyaltyConfig = {
                        type,
                        spendingRatio: generosity,
                        generosity,
                        targetCount: type === 'stamps' ? stampsTarget : pointsTarget,
                        targetSpending: program.targetSpending,
                        welcomeBonus: program.welcomeBonus ?? 0,
                        rewardLabel: program.rewardLabel || (type === 'stamps' ? 'Repas offert' : 'Café offert'),
                        missions: program.missions || [],
                        rewardTiers: Array.isArray(program.rewardTiers) ? program.rewardTiers : [],
                        grades: Array.isArray(program.grades) && program.grades.length > 0
                            ? dedupeGradeIds(program.grades)
                            : (type === 'stamps' ? DEFAULT_GRADES_STAMPS : DEFAULT_GRADES),
                        boost: program.boost && typeof program.boost === 'object'
                            ? program.boost
                            : DEFAULT_BOOST,
                    };
                    setConfig(loaded);
                    setInitialType(type);
                }
                setLoaded(true);
            } catch (error) {
                console.error('Error loading loyalty program:', error);
                setLoaded(false);
                setLoadError('Votre programme n\'a pas pu être chargé. Vérifiez la connexion puis réessayez : la publication reste bloquée d\'ici là.');
            } finally {
                setIsLoading(false);
            }
    }, []);

    useEffect(() => { loadProgram(); }, [loadProgram]);

    // Conversion des membres restée en suspens (appel échoué après un changement de type).
    const retryMigration = async () => {
        if (!pendingMigrationFrom) return;
        setRetryingMigration(true);
        setMigrationRetryResult(null);
        try {
            const backend = await getBackendService();
            const user = backend.getCurrentUser();
            const { migratedCount } = await (backend as any).migrateMemberships(user?.restaurantId, pendingMigrationFrom);
            setPendingMigrationFrom(null);
            setMigrationRetryResult(`Conversion terminée : ${migratedCount} client(s) converti(s).`);
        } catch (error: any) {
            setMigrationRetryResult(`La conversion a encore échoué : ${error?.message ?? 'erreur inconnue'}`);
        } finally {
            setRetryingMigration(false);
        }
    };

    // ── Type switch (Points ↔ Tampons) ────────────────────────────────────────
    const handleTypeChange = (newType: LoyaltyProgramType) => {
        if (newType !== 'points' && newType !== 'stamps') return;
        if (newType === config.type) return;
        const fromType = config.type;
        const newGrades = migrateGradeThresholds(config.grades || DEFAULT_GRADES, fromType, newType);
        // rewardTiers structure incompatible entre Points/Tampons → reset
        const tiersErased = (config.rewardTiers?.length ?? 0) > 0;
        setConfig(prev => ({
            ...prev,
            type: newType,
            targetCount: newType === 'stamps' ? 10 : 50,
            rewardLabel: newType === 'stamps' ? 'Repas offert' : 'Café offert',
            rewardTiers: [],
            grades: newGrades,
            // Si on passe à Points, le boost initial_stamps n'a plus de sens
            boost: prev.boost?.mode === 'initial_stamps' && newType === 'points'
                ? { enabled: false }
                : prev.boost,
        }));
        const messages: string[] = ['Seuils des grades adaptés au mode ' + (newType === 'stamps' ? 'Tampons' : 'Points')];
        if (tiersErased) messages.push('Paliers de récompenses effacés (à reconfigurer)');
        setMigrationToast(messages.join(' · '));
        setTimeout(() => setMigrationToast(null), 4000);
    };

    // ── Sub-state mapping ─────────────────────────────────────────────────────
    const pointsState: PointsConfigState = useMemo(() => ({
        generosity: config.generosity ?? 1,
        rewardLabel: config.rewardLabel || '',
        targetPoints: config.targetCount ?? 50,
        rewardTiers: config.rewardTiers || [],
    }), [config.generosity, config.rewardLabel, config.targetCount, config.rewardTiers]);

    const stampsState: StampsConfigState = useMemo(() => ({
        targetCount: config.targetCount ?? 10,
        rewardLabel: config.rewardLabel || '',
        rewardTiers: config.rewardTiers || [],
    }), [config.targetCount, config.rewardLabel, config.rewardTiers]);

    const setPointsState = (next: PointsConfigState) => {
        setConfig(prev => ({
            ...prev,
            generosity: next.generosity,
            spendingRatio: next.generosity, // alias rétro-compat
            rewardLabel: next.rewardLabel,
            targetCount: next.targetPoints,
            rewardTiers: next.rewardTiers,
        }));
    };

    const setStampsState = (next: StampsConfigState) => {
        setConfig(prev => ({
            ...prev,
            targetCount: next.targetCount,
            rewardLabel: next.rewardLabel,
            rewardTiers: next.rewardTiers,
        }));
    };

    const setGrades = (grades: LoyaltyGrade[]) => setConfig(prev => ({ ...prev, grades }));
    const setBoost = (boost: LoyaltyBoost) => setConfig(prev => ({ ...prev, boost }));

    // ── Validation ────────────────────────────────────────────────────────────
    const validation = useMemo(() => {
        const errors: { points?: string[]; stamps?: string[]; grades?: string[]; boost?: string[] } = {};

        if (config.type === 'points') {
            const e: string[] = [];
            const g = config.generosity ?? 1;
            if (g < 1 || g > 10) e.push('Générosité hors plage [1, 10]');
            if (!config.rewardLabel?.trim()) e.push('Récompense principale manquante');
            if ((config.targetCount ?? 0) < 1) e.push('Seuil de récompense invalide');
            const tiers = config.rewardTiers || [];
            if (tiers.length > 3) e.push('Maximum 3 paliers additionnels');
            tiers.forEach((t, i) => {
                if (!t.label.trim()) e.push(`Palier #${i + 1} sans libellé`);
                if (t.threshold <= (config.targetCount ?? 0)) e.push(`Palier #${i + 1} doit être > seuil principal`);
            });
            if (e.length) errors.points = e;
        }

        if (config.type === 'stamps') {
            const e: string[] = [];
            const t = config.targetCount ?? 10;
            if (t < 5 || t > 20) e.push('Objectif hors plage [5, 20]');
            if (!config.rewardLabel?.trim()) e.push('Récompense finale manquante');
            (config.rewardTiers || []).forEach(tier => {
                if (!tier.label.trim()) e.push(`Récompense intermédiaire au tampon ${tier.threshold} sans libellé`);
                if (tier.threshold >= t) e.push(`Récompense intermédiaire (${tier.threshold}) doit être < objectif (${t})`);
            });
            if (e.length) errors.stamps = e;
        }

        // Grades : seuils strictement croissants
        const grades = config.grades || [];
        if (grades.length === 0) {
            errors.grades = ['Au moins un grade est requis'];
        } else {
            const e: string[] = [];
            if (!grades[0].name.trim()) e.push('Le premier niveau doit avoir un nom');
            for (let i = 1; i < grades.length; i++) {
                if (grades[i].threshold <= grades[i - 1].threshold) {
                    e.push(`Le seuil de "${grades[i].name}" doit être supérieur à "${grades[i - 1].name}"`);
                }
                if (!grades[i].name.trim()) e.push(`Niveau ${i + 1} sans nom`);
            }
            // Identifiants et noms uniques : ils servent de clé (écran, notifications de montée).
            const ids = new Set<string>();
            const names = new Set<string>();
            grades.forEach((g, i) => {
                if (!g.id || ids.has(g.id)) e.push(`Niveau ${i + 1} : identifiant en double, supprimez-le puis recréez-le`);
                ids.add(g.id);
                const n = g.name.trim().toLowerCase();
                if (n && names.has(n)) e.push(`Deux niveaux s'appellent « ${g.name.trim()} »`);
                names.add(n);
            });
            if (e.length) errors.grades = e;
        }

        // Boost
        if (config.boost?.enabled) {
            const e: string[] = [];
            if (!config.boost.mode) e.push('Mode de boost requis');
            if (config.boost.mode === 'initial_stamps') {
                if (config.type !== 'stamps') e.push('Mode "Tampons offerts" disponible uniquement avec le type Tampons');
                const n = config.boost.initialStamps ?? 0;
                if (n < 1 || n > 3) e.push('Tampons offerts doit être entre 1 et 3');
            }
            if (e.length) errors.boost = e;
        }

        return errors;
    }, [config]);

    const hasErrors = Object.values(validation).some(arr => Array.isArray(arr) && arr.length > 0);

    // ── Save flow ─────────────────────────────────────────────────────────────
    const handleSave = async () => {
        if (hasErrors || !loaded) return;
        setSaveResult(null);
        // Le type enregistré peut avoir changé depuis le chargement (autre onglet) : la
        // fenêtre de confirmation annonce la conversion d'après l'état réel.
        setCheckingBeforeSave(true);
        try {
            const backend = await getBackendService();
            const user = backend.getCurrentUser();
            if (user?.restaurantId && (backend as any).getLoyaltyProgram) {
                const saved = await (backend as any).getLoyaltyProgram(user.restaurantId);
                if (saved?.type) setInitialType(saved.type as LoyaltyProgramType);
            }
            setIsMigrationModalOpen(true);
        } catch (error) {
            console.error('Error re-reading loyalty program:', error);
            setLoadError('Impossible de vérifier le programme enregistré avant de publier. Réessayez.');
        } finally {
            setCheckingBeforeSave(false);
        }
    };

    const confirmSave = async () => {
        setIsSaving(true);
        try {
            const backend = await getBackendService();
            const user = backend.getCurrentUser();
            if (!user?.restaurantId) throw new Error('Restaurant non trouvé');

            // Au save, on synchronise spendingRatio ← generosity pour rétro-compat runtime
            const payload: LoyaltyConfig = {
                ...config,
                spendingRatio: config.generosity ?? config.spendingRatio ?? 1,
            };

            const { previousType } = await (backend as any).saveLoyaltyProgram(user.restaurantId, payload);
            setInitialType(config.type);
            setDirty(false);

            if (previousType && previousType !== config.type) {
                try {
                    const { migratedCount } = await (backend as any).migrateMemberships(
                        user.restaurantId, previousType, config.type, payload,
                    );
                    setPendingMigrationFrom(null);
                    setSaveResult(`Programme publié. ${migratedCount} client(s) converti(s) vers le nouveau programme.`);
                } catch (error: any) {
                    // Programme enregistré, conversion à relancer (bandeau « Relancer la conversion »).
                    setPendingMigrationFrom(previousType);
                    setSaveResult(`Erreur : programme publié, mais la conversion de vos clients a échoué (${error?.message ?? 'erreur inconnue'}). Relancez-la depuis le bandeau.`);
                }
            } else {
                setSaveResult('Programme publié.');
            }
        } catch (error: any) {
            console.error('Error saving program:', error);
            setSaveResult(`Erreur : ${error?.message ?? 'inconnue'}`);
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-96" role="status">
                <SpinnerIcon className="w-12 h-12 text-brand-primary" />
                <span className="sr-only">Chargement du programme…</span>
            </div>
        );
    }

    if (!loaded && loadError) {
        return (
            <div className="space-y-6">
                <PageHeader title="Programme de fidélité" />
                <div role="alert" className="rounded-2xl border border-brand-200 bg-brand-50 p-5">
                    <p className="font-semibold text-brand-800">{loadError}</p>
                    <Button type="button" variant="primary" size="md" className="mt-3" onClick={loadProgram}>Réessayer</Button>
                </div>
            </div>
        );
    }

    const typeChanged = config.type !== initialType;
    const unit = config.type === 'stamps' ? 'tampons' : 'points';

    // Résumés d'étape (affichés quand l'étape est repliée)
    const summaries: Record<number, string> = {
        1: config.type === 'points' ? 'Points — le client cumule des points par euro dépensé'
            : config.type === 'stamps' ? 'Tampons — une visite = un tampon'
                : config.type === 'spending' ? 'Cagnotte (prochainement)' : 'Missions (prochainement)',
        2: config.type === 'stamps'
            ? `${config.rewardLabel || 'Récompense'} au ${config.targetCount ?? 10}e tampon${(config.rewardTiers?.length ?? 0) ? ` · ${config.rewardTiers!.length} récompense(s) intermédiaire(s)` : ''}`
            : `${config.generosity ?? 1} pt/€ · ${config.rewardLabel || 'Récompense'} à ${config.targetCount ?? 50} pts${(config.rewardTiers?.length ?? 0) ? ` · ${config.rewardTiers!.length} palier(s) de plus` : ''}`,
        3: `${(config.grades ?? []).length} niveau(x) : ${(config.grades ?? []).map(g => g.name).join(' · ')}`,
        4: config.boost?.enabled
            ? (config.boost.mode === 'initial_stamps' ? `${config.boost.initialStamps ?? 1} tampon(s) offert(s) à l'inscription`
                : config.boost.mode === 'first_week' ? 'Points doublés la première semaine' : 'Premier passage boosté')
            : 'Désactivé',
    };
    const stepErrors: Record<number, string[] | undefined> = {
        2: config.type === 'points' ? validation.points : validation.stamps,
        3: validation.grades,
        4: validation.boost,
    };

    return (
        <div className="space-y-6 sm:space-y-8">
            <PageHeader
                title="Programme de fidélité"
                subtitle="Points ou tampons, récompenses, niveaux, boost de bienvenue. L'aperçu montre la carte telle que vos clients la voient."
                actions={
                    <>
                        {dirty && !hasErrors && !isLegacyType && <span className="text-sm text-gray-500">Modifications non publiées</span>}
                        <Button type="button" variant="primary" size="md" onClick={handleSave} loading={checkingBeforeSave} disabled={hasErrors || isLegacyType || !loaded || checkingBeforeSave}>
                            Publier le programme
                        </Button>
                    </>
                }
            />

            {loadError && (
                <div role="alert" className="rounded-2xl border border-brand-200 bg-brand-50 px-4 py-3 text-sm text-brand-800 flex flex-wrap items-center gap-3">
                    <span className="flex-1">{loadError}</span>
                    <Button type="button" size="sm" variant="outline" onClick={() => setLoadError(null)}>Fermer</Button>
                </div>
            )}

            {pendingMigrationFrom && (
                <div role="alert" className="rounded-2xl border border-warning/40 bg-warning-bg p-4">
                    <p className="inline-flex items-center gap-2 text-sm font-semibold text-gray-900"><WarningIcon className="h-4 w-4" /> Conversion des clients non terminée</p>
                    <p className="mt-1 text-sm text-gray-800">
                        Le programme a changé de type mais la progression de vos clients n'a pas encore été convertie
                        ({pendingMigrationFrom === 'stamps' ? 'tampons' : 'points'} → {config.type === 'stamps' ? 'tampons' : 'points'}).
                        Leurs soldes affichés peuvent être faux tant que la conversion n'a pas abouti.
                    </p>
                    <Button type="button" size="sm" variant="primary" className="mt-3" onClick={retryMigration} loading={retryingMigration} disabled={retryingMigration}>
                        Relancer la conversion
                    </Button>
                </div>
            )}
            {migrationRetryResult && <p role="status" className="text-sm text-gray-800">{migrationRetryResult}</p>}

            {isLegacyType && (
                <div className="rounded-2xl border border-warning/30 bg-warning-bg p-4">
                    <p className="mb-1 inline-flex items-center gap-2 text-sm font-semibold text-warning"><WarningIcon className="h-4 w-4" /> Type prochainement disponible</p>
                    <p className="mb-3 text-sm text-gray-700">
                        Le type « {config.type === 'spending' ? 'Cagnotte' : 'Missions'} » est en cours de finalisation. La configuration est verrouillée ; vous pouvez migrer dès maintenant vers Points ou Tampons.
                    </p>
                    <div className="flex gap-2">
                        <Button type="button" size="sm" variant="outline" onClick={() => handleTypeChange('points')}>Migrer vers Points</Button>
                        <Button type="button" size="sm" variant="outline" onClick={() => handleTypeChange('stamps')}>Migrer vers Tampons</Button>
                    </div>
                </div>
            )}

            {migrationToast && (
                <div className="rounded-2xl border border-info/30 bg-info-bg px-4 py-3 text-sm text-info">{migrationToast}</div>
            )}

            {/* Sous xl, l'aperçu ne tient pas à côté des étapes : un bouton l'affiche en haut,
                au lieu de le laisser tout en bas, sous les quatre étapes. */}
            <div className="xl:hidden">
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    aria-expanded={showPreview}
                    aria-controls="program-preview-compact"
                    onClick={() => setShowPreview(v => !v)}
                >
                    {showPreview ? "Masquer l'aperçu" : "Voir l'aperçu côté client"}
                </Button>
                {showPreview && (
                    <div id="program-preview-compact" className="mt-4">
                        <ProgramPreview config={config} restaurantName={restaurantName} />
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_340px] xl:items-start">
                <div className="min-w-0 space-y-3">
                    <Step n={1} open={openStep === 1} onToggle={() => setOpenStep(openStep === 1 ? 0 : 1)} summary={summaries[1]} errors={stepErrors[1]} title="Type de programme" hint="Points ou tampons ? Les autres modes arrivent bientôt.">
                        <TypeSelector value={config.type} onChange={handleTypeChange} disabled={isLegacyType} />
                    </Step>

                    <Step n={2} open={openStep === 2} onToggle={() => setOpenStep(openStep === 2 ? 0 : 2)} summary={summaries[2]} errors={stepErrors[2]} title={config.type === 'stamps' ? 'Objectif et récompenses' : 'Règle et récompenses'} hint={config.type === 'stamps' ? "Combien de tampons pour la récompense, et lesquelles en chemin." : "Combien de points par euro, et ce qu'ils permettent d'obtenir."}>
                        {config.type === 'points' && <PointsConfig value={pointsState} onChange={setPointsState} errors={validation.points} />}
                        {config.type === 'stamps' && <StampsConfig value={stampsState} onChange={setStampsState} errors={validation.stamps} />}
                        {isLegacyType && <p className="text-sm text-gray-500">Disponible après migration vers Points ou Tampons.</p>}
                    </Step>

                    <Step n={3} open={openStep === 3} onToggle={() => setOpenStep(openStep === 3 ? 0 : 3)} summary={summaries[3]} errors={stepErrors[3]} title="Niveaux de fidélité" hint={`Des paliers en ${unit}. Le client monte tout seul et reçoit une notification.`}>
                        <GradesConfig grades={config.grades || DEFAULT_GRADES} onChange={setGrades} programType={config.type} errors={validation.grades} disabled={isLegacyType} />
                    </Step>

                    <Step n={4} open={openStep === 4} onToggle={() => setOpenStep(openStep === 4 ? 0 : 4)} summary={summaries[4]} errors={stepErrors[4]} title="Boost de bienvenue" hint="Un coup de pouce aux nouveaux membres pour déclencher la deuxième visite.">
                        <BoostConfig value={config.boost || DEFAULT_BOOST} onChange={setBoost} programType={config.type} />
                        {validation.boost && validation.boost.length > 0 && (
                            <div className="mt-3 space-y-1 rounded-xl border border-brand-200 bg-brand-50 p-3">
                                {validation.boost.map((err, i) => (
                                    <p key={i} className="inline-flex items-center gap-1.5 text-xs text-brand-700"><WarningIcon className="h-3.5 w-3.5 flex-shrink-0" /> {err}</p>
                                ))}
                            </div>
                        )}
                    </Step>
                </div>

                <aside className="hidden xl:block xl:sticky xl:top-24" aria-label="Aperçu côté client">
                    <ProgramPreview config={config} restaurantName={restaurantName} />
                </aside>
            </div>

            {/* Modal de confirmation save / migration */}
            <Modal
                isOpen={isMigrationModalOpen}
                onClose={() => { setIsMigrationModalOpen(false); setSaveResult(null); }}
                title={typeChanged ? 'Changement de programme' : 'Publier le programme'}
                dismissible={!isSaving}
            >
                <div>
                    {!saveResult ? (
                        <>
                            {typeChanged ? (
                                <div className="mb-5 rounded-xl border border-warning/40 bg-warning-bg p-4">
                                    <p className="inline-flex items-center gap-2 text-sm font-semibold text-gray-900"><WarningIcon className="h-4 w-4" /> Changement de type : {initialType === 'stamps' ? 'Tampons' : 'Points'} → {config.type === 'stamps' ? 'Tampons' : 'Points'}</p>
                                    <p className="mt-1 text-sm text-gray-800">
                                        La progression de chaque client est convertie au prorata de l'objectif de l'ancien programme
                                        (par exemple la moitié de l'objectif devient la moitié du nouveau), sans jamais débloquer
                                        de récompense : au plus un cran sous la première. L'ancien solde est conservé dans la fiche.
                                    </p>
                                </div>
                            ) : (
                                <p className="mb-5 text-sm text-gray-700">Le programme <b>{config.type === 'stamps' ? 'Tampons' : 'Points'}</b> sera mis à jour pour tous vos clients.</p>
                            )}
                            <p className="mb-5 text-sm text-gray-600">Les modifications seront visibles dans l'app juste après la publication.</p>
                            <div className="flex gap-3">
                                <Button type="button" variant="ghost" size="md" onClick={() => setIsMigrationModalOpen(false)} disabled={isSaving} className="flex-1">Annuler</Button>
                                <Button type="button" variant="primary" size="md" onClick={confirmSave} disabled={isSaving} loading={isSaving} className="flex-[2]">Confirmer</Button>
                            </div>
                        </>
                    ) : (
                        <>
                            <div role={saveResult.startsWith('Erreur') ? 'alert' : 'status'} className={`mb-5 rounded-xl p-3 ${saveResult.startsWith('Erreur') ? 'bg-brand-50 text-brand-800' : 'bg-success-bg text-gray-900'}`}>
                                <p className="inline-flex items-center gap-2 text-sm font-semibold">
                                    {saveResult.startsWith('Erreur') ? <XIcon className="h-4 w-4" /> : <CheckIcon className="h-4 w-4" />}
                                    {saveResult}
                                </p>
                            </div>
                            <Button type="button" variant="primary" size="md" fullWidth onClick={() => { setIsMigrationModalOpen(false); setSaveResult(null); }}>Fermer</Button>
                        </>
                    )}
                </div>
            </Modal>
        </div>
    );
};

export default LoyaltyProgram;
